import React, { useState } from 'react'
import Card from '@material-ui/core/Card'
import { useHistory } from 'react-router-dom'
import CardHeader from '@material-ui/core/CardHeader'
import IconButton from '@material-ui/core/IconButton'
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined'
import { leadCardDTOWire } from './wizard/lead-card.wire'
import { UserLayout } from 'components/layout/UserLayout'
import { useCreateFetchHookAdvanced } from 'hooks/create-fetch-hook-advanced.base'
import CardActionArea from '@material-ui/core/CardActionArea'
import { LeadCardPreview } from './lead-card-preview/LeadCardPreview'
import { leadCardService, synchronizeDataService, useGetBusiness } from '@marketing-milk/frontend'
import { toast } from 'react-toastify'
import { formTypeWire } from './lead-card-preview/LeadCardPreview.types'
import { ListPageBar } from 'components/layout/PageBar'
import { Button, CircularProgress, Tooltip } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import { Skeleton } from '@mui/material'
import { FullPageContent } from 'components/layout/FullPageLayout/Content/FullPageContent'
import { useScreenSize } from 'hooks'
import { isEmpty, range } from 'lodash'
import { NoItemsCreated } from '../../../components/NoItemsCreated/NoItemsCreated'

// The preview renders a real lead form at its native 322×600 and every other consumer
// wants that size, so the gallery scales it down. Showing all 600 makes a wall of type too
// small to read and leaves dead ground under a short form, so the tile shows the top of the
// form instead — image, business, headline, first lines — and fades out rather than cutting,
// which is what tells you which form this is.
//
// The preview's width is derived from the tile's, so the two cannot drift apart and leave
// the preview wider than the card clipping it. Both are in px: a rem is not 16px here,
// because the legacy Metronic stylesheet resets the root font size.
const TILE_WIDTH = 240
const TILE_PADDING = 8
const PREVIEW_WIDTH = TILE_WIDTH - TILE_PADDING * 2
const PREVIEW_SCALE = PREVIEW_WIDTH / 322
const VISIBLE_FORM_HEIGHT = 430
const PREVIEW_HEIGHT = Math.round(VISIBLE_FORM_HEIGHT * PREVIEW_SCALE)

export function ListLeadCards() {
  const history = useHistory()
  const business = useGetBusiness()
  const [filter, setFilter] = useState('')
  const [refresh, setRefresh] = useState(0)
  const [isSyncing, setIsSyncing] = useState(false)
  // The advanced hook because it reports failure; the plain one never leaves undefined when the
  // request fails, which here would mean skeletons forever.
  const { data: leadCards, error: loadError } = useCreateFetchHookAdvanced(
    () => leadCardService.getAll(business.id),
    business.id + refresh
  )
  const isLoading = !leadCards && !loadError
  const filteredLeadCards = (leadCards ?? []).filter(_ =>
    _.formName.toLowerCase().includes(filter.trim().toLowerCase())
  )
  const { isMobile } = useScreenSize()

  async function syncLeadsWithFB() {
    if (isSyncing) return

    setIsSyncing(true)
    try {
      await synchronizeDataService.syncLeadgenForms(business.id)
      setRefresh(value => value + 1)
      toast.success('Lead cards synced with Facebook.')
    } catch (e) {
      toast.error('Could not sync lead cards with Facebook. Please try again.')
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <UserLayout>
      <ListPageBar
        subject="Lead Cards"
        search={{
          value: filter,
          onChange: setFilter,
          placeholder: 'Search lead cards',
        }}
        actions={
          <>
            <Button
              variant="contained"
              color="primary"
              aria-label="Create lead card"
              onClick={() => history.push(`/${business.id}/lead-cards/create`)}
            >
              {isMobile ? '+' : 'Create Lead Card'}
            </Button>
            <IconButton
              onClick={syncLeadsWithFB}
              title="Sync with Facebook"
              aria-label={isSyncing ? 'Syncing with Facebook' : 'Sync with Facebook'}
              disabled={isSyncing}
            >
              {isSyncing ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon />}
            </IconButton>
          </>
        }
        status={
          <span role="status">
            {isSyncing
              ? 'Syncing lead cards with Facebook…'
              : isLoading
              ? 'Loading lead cards…'
              : leadCards
              ? `Showing ${filteredLeadCards.length} of ${leadCards.length} lead cards`
              : undefined}
          </span>
        }
      />

      <FullPageContent className="flex w-full flex-col p-4">
        {(isLoading || !isEmpty(filteredLeadCards)) && (
          <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] gap-4">
            {isLoading
              ? range(0, 12).map(i => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    height={PREVIEW_HEIGHT + 70}
                    className="w-full rounded"
                  />
                ))
              : filteredLeadCards.map(leadCard => (
                  <Card key={leadCard.id} className="flex h-full w-full flex-col">
                    <CardHeader
                      classes={{ content: 'min-w-0' }}
                      title={leadCard.formName}
                      titleTypographyProps={{
                        variant: 'subtitle2',
                        noWrap: true,
                        title: leadCard.formName,
                      }}
                      subheader={formTypeWire[leadCard.formType]}
                      subheaderTypographyProps={{ variant: 'caption', noWrap: true }}
                      action={
                        <Tooltip title="Duplicate lead card">
                          <IconButton
                            size="small"
                            aria-label={`Duplicate ${leadCard.formName}`}
                            onClick={() =>
                              history.push(`/${business.id}/lead-cards/duplicate/${leadCard.id}`)
                            }
                          >
                            <ContentCopyOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    />
                    <CardActionArea
                      className="flex-1"
                      aria-label={`Edit ${leadCard.formName}`}
                      onClick={() => history.push(`/${business.id}/lead-cards/${leadCard.id}`)}
                    >
                      <div className="flex justify-center" style={{ padding: TILE_PADDING }}>
                        <div
                          className="pointer-events-none relative max-w-full overflow-hidden rounded"
                          style={{ width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT }}
                        >
                          <div
                            style={{
                              transform: `scale(${PREVIEW_SCALE})`,
                              transformOrigin: 'top left',
                            }}
                          >
                            <LeadCardPreview
                              leadCard={leadCardDTOWire(leadCard)}
                              business={business}
                              mobilePreview={false}
                              selectionPreview={false}
                              showName={false}
                            />
                          </div>
                          <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-white" />
                        </div>
                      </div>
                    </CardActionArea>
                  </Card>
                ))}
          </div>
        )}

        {!isLoading && isEmpty(filteredLeadCards) && (
          <div className="flex w-full justify-center">
            {loadError ? (
              <NoItemsCreated
                title="Lead cards could not be loaded"
                subtitle="Something went wrong fetching this business's lead cards."
              >
                <Button variant="outlined" color="primary" onClick={() => setRefresh(v => v + 1)}>
                  Try again
                </Button>
              </NoItemsCreated>
            ) : filter.trim() ? (
              <NoItemsCreated
                title="No lead cards match this search"
                subtitle={`Nothing here is named like “${filter}”.`}
              >
                <Button variant="outlined" color="primary" onClick={() => setFilter('')}>
                  Clear search
                </Button>
              </NoItemsCreated>
            ) : (
              <NoItemsCreated
                title="No lead cards yet"
                subtitle="Build one here, or sync the forms this business already has on Facebook."
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => history.push(`/${business.id}/lead-cards/create`)}
                >
                  Create lead card
                </Button>
              </NoItemsCreated>
            )}
          </div>
        )}
      </FullPageContent>
    </UserLayout>
  )
}
